import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, ImageIcon, Loader2, Plus, Send, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';
import { apiErrorMessage } from '../../../lib/apiError';

/**
 * Track B — marketing recipients.
 *
 * Addresses that sit outside every client account and receive each menu
 * announcement as product names, descriptions and full-size image links with
 * NO pricing anywhere. Admin-managed; lives beside the Menu Calendar because
 * that is where announcements are sent from.
 */
const MarketingRecipientsPanel = () => {
  const [recipients, setRecipients] = useState([]);
  const [maxRecipients, setMaxRecipients] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', label: '' });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getMarketingRecipients();
      setRecipients(data.recipients || []);
      if (data.max_recipients) setMaxRecipients(data.max_recipients);
    } catch {
      toast.error('Failed to load marketing recipients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.email.trim() || !form.label.trim()) {
      toast.error('Both an email address and a label are required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await adminService.addMarketingRecipient({
        email: form.email.trim(), label: form.label.trim(),
      });
      toast.success(`${form.email.trim()} will receive menu assets`);
      setForm({ email: '', label: '' });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add recipient'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (r) => {
    setBusyId(r.id);
    try {
      await adminService.updateMarketingRecipient(r.id, { active: !(r.active ?? true) });
      toast.success(r.active ? 'Recipient muted' : 'Recipient unmuted');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update recipient'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (r) => {
    if (!window.confirm(`Stop sending menu assets to ${r.email}?`)) return;
    setBusyId(r.id);
    try {
      await adminService.deleteMarketingRecipient(r.id);
      toast.success('Recipient removed');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove recipient'));
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = async (r) => {
    setBusyId(r.id);
    try {
      const res = await adminService.testMarketingRecipient(r.id);
      toast.success(
        `Test sent to ${res.email} — "${res.menu_title}" (${res.products} products, ${res.asset_links} image links)`
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to send test email'));
    } finally {
      setBusyId(null);
    }
  };

  const atCap = recipients.length >= maxRecipients;

  return (
    <Card data-testid="marketing-recipients-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-amber-600" />
            Marketing recipients
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Every menu announcement is also sent to these addresses as product names,
            descriptions and full-size image links — with <b>no pricing anywhere</b>.
            For a social media manager or marketing contractor who isn&apos;t a client.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 self-start"
          disabled={atCap}
          onClick={() => setShowAdd(true)}
          data-testid="marketing-add-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add recipient
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : recipients.length === 0 ? (
          <div className="text-center py-8" data-testid="marketing-empty">
            <ImageIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No marketing recipients configured</p>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {recipients.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 p-3"
                data-testid={`marketing-row-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{r.label}</span>
                    {!(r.active ?? true) && (
                      <Badge variant="outline" className="text-gray-500" data-testid={`marketing-muted-${r.id}`}>
                        Muted
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{r.email}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    title="Send the latest published menu to this address"
                    disabled={busyId === r.id}
                    onClick={() => handleTest(r)}
                    data-testid={`marketing-test-${r.id}`}
                  >
                    {busyId === r.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><Send className="h-3.5 w-3.5 mr-1" /> Send test</>}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={r.active ?? true ? 'Mute (stop sending)' : 'Unmute'}
                    disabled={busyId === r.id}
                    onClick={() => handleToggle(r)}
                    data-testid={`marketing-toggle-${r.id}`}
                  >
                    {r.active ?? true
                      ? <Bell className="h-4 w-4 text-amber-600" />
                      : <BellOff className="h-4 w-4 text-gray-400" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    title="Remove"
                    disabled={busyId === r.id}
                    onClick={() => handleRemove(r)}
                    data-testid={`marketing-remove-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && (
          <p className="text-xs text-gray-400 mt-3" data-testid="marketing-cap">
            {recipients.length} of {maxRecipients} recipients used
            {atCap && ' — remove one before adding another'}
          </p>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="marketing-add-dialog">
          <DialogHeader>
            <DialogTitle>Add a marketing recipient</DialogTitle>
            <DialogDescription>
              They receive every menu announcement with images and descriptions and no
              pricing. They will not see any client&apos;s prices or place orders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="marketing-email">Email address *</Label>
              <Input
                id="marketing-email"
                type="email"
                value={form.email}
                placeholder="social@agency.com"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="marketing-email-input"
              />
            </div>
            <div>
              <Label htmlFor="marketing-label">Label *</Label>
              <Input
                id="marketing-label"
                value={form.label}
                placeholder="Social media manager"
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="marketing-label-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                The email address can&apos;t be edited afterwards — remove and re-add instead.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleAdd}
              disabled={submitting}
              data-testid="marketing-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add recipient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MarketingRecipientsPanel;
